import React, { useEffect, useState, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, UserCog, Plus, Pencil, Trash2,
  X, AlertTriangle, CheckCircle2, AlertCircle, Eye, EyeOff,
} from 'lucide-react';
import { adminsAPI } from '../../api/admins';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/ui/PageHeader';

// ── Helpers ───────────────────────────────────────────────────
const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// ── Role badge ────────────────────────────────────────────────
function RoleBadge({ role }) {
  const isSA = role === 'superadmin';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-[10px] font-bold"
      style={
        isSA
          ? { background: 'rgba(212,160,23,0.18)', color: '#f0c040', border: '1px solid rgba(212,160,23,0.35)' }
          : { background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.3)' }
      }
    >
      {isSA ? <ShieldCheck size={9} /> : <ShieldAlert size={9} />}
      {isSA ? 'Superadmin' : 'Manager'}
    </span>
  );
}

// ── Password input ────────────────────────────────────────────
function PasswordInput({ label, name, value, onChange, error, placeholder, required }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <label className="input-label">
        {label}{!required && <span style={{ color: 'rgba(255,255,255,0.3)' }}> (optional)</span>}
      </label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="new-password"
          className={`input-field pr-10 ${error ? 'border-red-400' : ''}`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2"
          tabIndex={-1}
        >
          {show
            ? <EyeOff size={15} style={{ color: 'rgba(255,255,255,0.35)' }} />
            : <Eye    size={15} style={{ color: 'rgba(255,255,255,0.35)' }} />}
        </button>
      </div>
      {error && <p className="text-xs" style={{ color: '#fca5a5' }}>{error}</p>}
    </div>
  );
}

// ── Admin Form Sheet (create / edit) ─────────────────────────
function AdminFormSheet({ admin, onClose, onSuccess }) {
  const isEdit = Boolean(admin);

  const [form, setForm] = useState({
    name:     admin?.name  ?? '',
    email:    admin?.email ?? '',
    role:     admin?.role  ?? 'manager',
    password: '',
  });
  const [errors,     setErrors]     = useState({});
  const [apiError,   setApiError]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (apiError) setApiError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())  errs.name  = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email';
    if (!isEdit && !form.password) errs.password = 'Password is required';
    if (form.password && form.password.length < 8) errs.password = 'Minimum 8 characters';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError('');
    try {
      const payload = {
        name:  form.name.trim(),
        email: form.email.trim(),
        role:  form.role,
      };
      if (form.password) payload.password = form.password;

      const { data } = isEdit
        ? await adminsAPI.update(admin.id, payload)
        : await adminsAPI.create(payload);

      if (data.status) {
        onSuccess(data.data, isEdit);
        onClose();
      } else {
        setApiError(data.message || 'Operation failed.');
      }
    } catch (err) {
      const errData = err?.response?.data;
      setApiError(
        errData?.errors?.email?.[0] ||
        errData?.message ||
        'Something went wrong.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                 style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)' }}>
              <UserCog size={17} style={{ color: '#d4a017' }} />
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold text-white">
                {isEdit ? 'Edit Admin' : 'Add Admin'}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {isEdit ? `Editing ${admin.name}` : 'Create a new admin account'}
              </p>
            </div>
          </div>
          {!submitting && (
            <button onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.08)' }}>
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        {apiError && (
          <div className="mb-4 flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
            <span>{apiError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className="input-label">Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. John Manager"
              className={`input-field ${errors.name ? 'border-red-400' : ''}`}
            />
            {errors.name && <p className="text-xs" style={{ color: '#fca5a5' }}>{errors.name}</p>}
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="input-label">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="admin@example.com"
              autoComplete="off"
              className={`input-field ${errors.email ? 'border-red-400' : ''}`}
            />
            {errors.email && <p className="text-xs" style={{ color: '#fca5a5' }}>{errors.email}</p>}
          </div>

          {/* Role */}
          <div>
            <p className="input-label mb-2">Role</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'superadmin', label: 'Superadmin', icon: ShieldCheck, desc: 'Full access' },
                { value: 'manager',   label: 'Manager',    icon: ShieldAlert,  desc: 'Cannot manage admins' },
              ].map(({ value, label, icon: Icon, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, role: value }))}
                  className="flex flex-col items-start gap-1 rounded-2xl p-3 text-left transition-all"
                  style={
                    form.role === value
                      ? value === 'superadmin'
                        ? { background: 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.4)' }
                        : { background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)' }
                      : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }
                  }
                >
                  <div className="flex items-center gap-1.5">
                    <Icon size={13} style={{
                      color: form.role === value
                        ? value === 'superadmin' ? '#f0c040' : '#a5b4fc'
                        : 'rgba(255,255,255,0.4)',
                    }} />
                    <span className="text-xs font-semibold text-white">{label}</span>
                  </div>
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Password */}
          <PasswordInput
            label="Password"
            name="password"
            value={form.password}
            onChange={handleChange}
            error={errors.password}
            placeholder={isEdit ? 'Leave blank to keep current' : 'Min. 8 characters'}
            required={!isEdit}
          />

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl py-3.5
                         text-sm font-semibold transition-all active:scale-95 disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg,#d4a017,#f0c040)',
                boxShadow: '0 4px 16px rgba(212,160,23,0.3)',
                color: '#1a0a00',
              }}
            >
              {submitting
                ? <span className="inline-block h-4 w-4 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                : isEdit ? 'Save Changes' : 'Create Admin'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete confirm sheet ──────────────────────────────────────
function DeleteSheet({ admin, onClose, onConfirm, submitting, error }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
               style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <AlertTriangle size={18} style={{ color: '#fca5a5' }} />
          </div>
          <div>
            <h3 className="font-serif text-base font-semibold text-white">Delete Admin</h3>
            <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {admin.name} · {admin.email}
            </p>
          </div>
        </div>

        <p className="text-sm mb-5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
          This will <span style={{ color: '#fca5a5', fontWeight: 600 }}>permanently delete</span> this
          admin account. They will be immediately signed out.
        </p>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl py-3.5
                       text-sm font-semibold transition-all active:scale-95 disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff', boxShadow: '0 4px 16px rgba(220,38,38,0.35)' }}
          >
            {submitting
              ? <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              : <><Trash2 size={15} /> Delete</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function AdminAccountsPage() {
  const { user } = useAuth();

  const [admins,     setAdmins]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // sheet state
  const [formTarget,   setFormTarget]   = useState(null);  // null=closed, false=create, {admin}=edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [deleteError,  setDeleteError]  = useState('');

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await adminsAPI.getList();
      if (data.status) setAdmins(data.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load admin accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  // Auto-dismiss success
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 3500);
    return () => clearTimeout(t);
  }, [successMsg]);

  const handleFormSuccess = (adminData, isEdit) => {
    if (isEdit) {
      setAdmins((prev) => prev.map((a) => a.id === adminData.id ? { ...a, ...adminData } : a));
      setSuccessMsg('Admin updated successfully.');
    } else {
      setAdmins((prev) => [...prev, adminData]);
      setSuccessMsg('Admin account created successfully.');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await adminsAPI.delete(deleteTarget.id);
      setAdmins((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      setSuccessMsg('Admin account deleted.');
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete admin.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0">
      <PageHeader
        title="Admin Accounts"
        showBack
        right={
          <button
            onClick={() => setFormTarget(false)}
            className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold
                       transition-all active:scale-95"
            style={{
              background: 'rgba(212,160,23,0.15)',
              border: '1px solid rgba(212,160,23,0.3)',
              color: '#d4a017',
            }}
          >
            <Plus size={13} />
            Add Admin
          </button>
        }
      />

      <div className="flex flex-col gap-3 p-4">

        {/* Success banner */}
        {successMsg && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
               style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.35)', color: '#6ee7b7' }}>
            <CheckCircle2 size={15} className="flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="card py-6 text-sm text-center" style={{ color: '#fca5a5' }}>{error}</div>
        )}

        {/* Skeleton */}
        {loading && !error && (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card flex items-center gap-3">
                <div className="skeleton h-10 w-10 rounded-full flex-shrink-0" />
                <div className="flex-1 flex flex-col gap-2">
                  <div className="skeleton h-3.5 w-28 rounded" />
                  <div className="skeleton h-3 w-40 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Admin list */}
        {!loading && !error && admins.map((a) => {
          const isSelf = a.id === user?.id;
          return (
            <div
              key={a.id}
              className="card flex items-center gap-3"
            >
              {/* Avatar */}
              <div
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold"
                style={{
                  background: a.role === 'superadmin'
                    ? 'linear-gradient(135deg,#b8860b,#d4a017)'
                    : 'linear-gradient(135deg,#4338ca,#6366f1)',
                  color: '#fff',
                }}
              >
                {a.name.charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-white truncate">{a.name}</p>
                  {isSelf && (
                    <span className="text-[10px] font-medium rounded-lg px-1.5 py-0.5"
                          style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>
                      You
                    </span>
                  )}
                  <RoleBadge role={a.role} />
                </div>
                <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {a.email}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  Added {formatDate(a.created_at)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setFormTarget(a)}
                  className="flex h-8 w-8 items-center justify-center rounded-xl transition-all active:scale-90"
                  style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)' }}
                >
                  <Pencil size={13} style={{ color: '#d4a017' }} />
                </button>
                {!isSelf && (
                  <button
                    onClick={() => { setDeleteError(''); setDeleteTarget(a); }}
                    className="flex h-8 w-8 items-center justify-center rounded-xl transition-all active:scale-90"
                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
                  >
                    <Trash2 size={13} style={{ color: '#fca5a5' }} />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Empty state */}
        {!loading && !error && admins.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <UserCog size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              No admin accounts found
            </p>
          </div>
        )}

      </div>

      {/* Form sheet */}
      {formTarget !== null && (
        <AdminFormSheet
          admin={formTarget || null}
          onClose={() => setFormTarget(null)}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete sheet */}
      {deleteTarget && (
        <DeleteSheet
          admin={deleteTarget}
          onClose={() => !deleting && setDeleteTarget(null)}
          onConfirm={handleDelete}
          submitting={deleting}
          error={deleteError}
        />
      )}
    </div>
  );
}
